import { defineStore } from "pinia";
import { io } from "socket.io-client";
import { inject, reactive, Reactive, toRaw } from "vue";
import { useRoute, useRouter } from "vue-router";
import axios from "axios";
import { useMainStore } from "./mainStore";
import { useCalculationStore } from "./calculationStore";
import { useSpaceStore } from "./spaceStore";
import { useTreeStore } from "./treeStore";
import { useAuthStore } from "./authStore";
import { isLocationType } from "@/utils/helper";

export const useSocketStore = defineStore("socketStore", () => {
  const router = useRouter();
  const route = useRoute();
  const rustWorker: Worker = inject("rustWorker");

  const mainStore = useMainStore();
  const calculationStore = useCalculationStore();
  const spaceStore = useSpaceStore();
  const treeStore = useTreeStore();
  const authStore = useAuthStore();

  const socket = io(axios.defaults.baseURL);

  // client-side
  socket.on("connect", () => {
    console.log("Websocket connected with id:", socket.id);
    axios.defaults.headers.common["Socket"] = socket.id;

    if (authStore.loggedIn) {
      socket.emit("user", authStore.user.id);

      if (mainStore.activeSpaceId) {
        socket.emit("enter-space", mainStore.activeSpaceId);

        if (route.meta.calc && route.params.nodeId) {
          socket.emit("element-active", route.params.nodeId);
        }
      }
    }
  });

  socket.on("disconnect", () => {
    console.log("Websocket disconnected");
    delete axios.defaults.headers.common["Socket"];
  });

  socket.on("element-focus", (userId, elementId, fieldName) => {
    console.log("Element focused:", elementId, fieldName);
    mainStore.occupiedFields[elementId] = {
      ...mainStore.occupiedFields[elementId],
      [fieldName]: userId
    };

    calculationStore.focusElementSocket(elementId, fieldName, userId);
  });

  socket.on("element-blur", (userId, elementId, fieldName) => {
    console.log("Element blurred:", elementId, fieldName);
    delete mainStore.occupiedFields[elementId][fieldName];
    calculationStore.executeFocusCalculationSocket(userId);
  });

  socket.on("element-level", (elementId, level) => {
    spaceStore.calculationElements[elementId].level = level;
    calculationStore.addElementCalculation(elementId, [], true);
    calculationStore.startCalculating();
  });

  socket.on("element-active", (userId, elementId) => {
    mainStore.activeUsers[userId] = elementId;
  });

  socket.on("add-space", () => {
    mainStore.loadCalculationSpaces();
  });

  socket.on("delete-space", (spaceId) => {
    mainStore.loadCalculationSpaces();
    if (spaceId === mainStore.activeSpaceId) {
      mainStore.activeSpaceId = null;
      router.push("/");
    }
  });

  socket.on("space-joined", (userId, spaceId) => {
    const space = mainStore.calculationSpaces.find(
      (space) => space.id === spaceId
    );
    if (space) {
      if (mainStore.activeSpaceId === space.id) {
        // Later
      } else if (mainStore.activeSpaceId === null) {
        mainStore.loadCalculationSpaces();
      } else {
        // do nothing, is in another space
      }
    }
  });

  socket.on("space-left", (userId, spaceId) => {
    console.log("User left space", spaceId);
    const space = mainStore.calculationSpaces.find(
      (space) => space.id === spaceId
    );
    if (space) {
      if (mainStore.activeSpaceId === space.id) {
        // Later
      } else if (mainStore.activeSpaceId === null) {
        mainStore.loadCalculationSpaces();
      } else {
        // do nothing, is in another space
      }
    }
  });

  socket.on("delete-space", (spaceId) => {
    const space = mainStore.calculationSpaces.find(
      (space) => space.id === spaceId
    );
    if (space) {
      if (mainStore.activeSpaceId === space.id) {
        mainStore.activeSpaceId = null;
      }
      mainStore.calculationSpaces = mainStore.calculationSpaces.filter(
        (space) => space.id !== spaceId
      );
    }
  });

  socket.on("missing-input-add", (values) => {
    console.log("Missing input added", values);

    spaceStore.missingInputs.push(...values);
  });

  socket.on("missing-input-remove", (value) => {
    console.log("Missing input removed", value);

    const [element, field] = value.split("|");
    spaceStore.removeMissingInput(element, field, false);
  });

  socket.on("field-input", (userId, elementId, fieldName, value) => {
    console.log("socket field input", elementId, fieldName, value);
    if (fieldName === "manufacturing_feature") {
      value = JSON.parse(value);
    }

    spaceStore.calculationElements[elementId][fieldName] = value;
    calculationStore.calculateOnlyCurrentSocket(
      elementId,
      fieldName,
      value,
      userId
    );

    if (["name", "variant_name", "manufacturing_name"].includes(fieldName)) {
      treeStore.changeInHierachy(elementId);
    }
  });

  socket.on("create-element", (userId, elementId, data, variant) => {
    if (spaceStore.calculationElements.hasOwnProperty(elementId)) {
      console.log("Created element already exists");
      // do nothing because creator is self
      return;
    }
    spaceStore.calculationElements[elementId] = data;
    console.log("Created element from Socket:", elementId, data);
    rustWorker.postMessage({
      task: "addElement",
      data: { id: elementId, data }
    });

    // calculationStore.addElementCalculation(
    //   elementId,
    //   [],
    //   true //userId == authStore.user.id
    // );
    // calculationStore.startCalculating();

    if (
      data.parent &&
      !spaceStore.calculationElements[data.parent].children.includes(elementId)
    ) {
      // only add child to parent if it not already exists
      spaceStore.calculationElements[data.parent].children.push(elementId);
      rustWorker.postMessage({
        task: "change",
        data: {
          id: spaceStore.calculationElements[elementId].parent,
          field: "children",
          value: toRaw(
            spaceStore.calculationElements[
              spaceStore.calculationElements[elementId].parent
            ].children
          )
        }
      });
    }

    if (elementId.endsWith("part")) {
      const elementList = spaceStore
        .getElementList(data.parent, "part")
        .filter((element) => element.variant === data.variant);

      if (elementList.length > 1) {
        variant = elementList[0].id + "_part";
      }
    }

    if (!isLocationType(elementId)) {
      if (variant) {
        treeStore.addVariantToHierarchy(elementId, variant);
      } else {
        treeStore.addToHierarchy(elementId, data.parent);
      }
    }
  });

  socket.on("delete-element", (userId, elementId, variant) => {
    console.log("Deleted element socket", elementId, variant);
    spaceStore.removeElement(elementId, undefined, variant, true);
  });

  socket.on("move-element", (elementId, newParent) => {
    spaceStore.moveElement(elementId, newParent, true);
  });

  return {
    socket
  };
});
